/**
 * TTL memoization over a pluggable store.
 *
 * The cache does not know how to fetch anything, and the data-source packages do
 * not know how to cache. Callers wire the two together, choosing a store that
 * suits the runtime: memory for build scripts, IndexedDB for the browser.
 */

export type CacheEntry = { value: unknown; expiresAt: number };

export type CacheStore = {
  get(key: string): Promise<CacheEntry | undefined>;
  set(key: string, entry: CacheEntry): Promise<void>;
  delete(key: string): Promise<void>;
  clear(): Promise<void>;
  /**
   * Every stored key. Expiry is only noticed on read, so without a way to walk
   * the store an entry whose key is never asked for again is never released.
   */
  keys(): Promise<string[]>;
};

export type Cache = {
  /** Return the cached value for `key`, otherwise run `fn` and cache its result. */
  wrap<T>(key: string, ttlMs: number, fn: () => Promise<T>): Promise<T>;
  get<T>(key: string): Promise<T | undefined>;
  set<T>(key: string, value: T, ttlMs: number): Promise<void>;
  delete(key: string): Promise<void>;
  clear(): Promise<void>;
  /** Drop every entry already past its expiry. Returns how many were removed. */
  prune(): Promise<number>;
};

export function createCache(store: CacheStore, namespace = ""): Cache {
  const scoped = (key: string): string => (namespace ? `${namespace}:${key}` : key);
  /** Collapse concurrent misses for the same key into a single upstream call. */
  const inflight = new Map<string, Promise<unknown>>();

  const get = async <T>(key: string): Promise<T | undefined> => {
    const entry = await store.get(scoped(key));
    if (!entry) return undefined;
    if (entry.expiresAt <= Date.now()) {
      await store.delete(scoped(key));
      return undefined;
    }
    return entry.value as T;
  };

  return {
    get,

    async set<T>(key: string, value: T, ttlMs: number): Promise<void> {
      await store.set(scoped(key), { value, expiresAt: Date.now() + ttlMs });
    },

    async delete(key: string): Promise<void> {
      inflight.delete(scoped(key));
      await store.delete(scoped(key));
    },

    async clear(): Promise<void> {
      inflight.clear();
      await store.clear();
    },

    async prune(): Promise<number> {
      const now = Date.now();
      let removed = 0;
      for (const key of await store.keys()) {
        const entry = await store.get(key);
        if (entry && entry.expiresAt <= now) {
          await store.delete(key);
          removed++;
        }
      }
      return removed;
    },

    async wrap<T>(key: string, ttlMs: number, fn: () => Promise<T>): Promise<T> {
      const hit = await get<T>(key);
      if (hit !== undefined) return hit;

      const pending = inflight.get(scoped(key));
      if (pending) return (await pending) as T;

      const promise = (async () => {
        const value = await fn();
        await store.set(scoped(key), { value, expiresAt: Date.now() + ttlMs });
        return value;
      })();

      inflight.set(scoped(key), promise);
      try {
        return await promise;
      } finally {
        inflight.delete(scoped(key));
      }
    },
  };
}

/** In-process store. Suitable for build scripts and tests. */
export function memoryStore(): CacheStore {
  const map = new Map<string, CacheEntry>();
  return {
    async get(key) {
      return map.get(key);
    },
    async set(key, entry) {
      map.set(key, entry);
    },
    async delete(key) {
      map.delete(key);
    },
    async clear() {
      map.clear();
    },
    async keys() {
      return [...map.keys()];
    },
  };
}

/** Store that never retains anything, for disabling caching without branching. */
export function nullStore(): CacheStore {
  return {
    async get() {
      return undefined;
    },
    async set() {},
    async delete() {},
    async clear() {},
    async keys() {
      return [];
    },
  };
}

/**
 * Browser-persistent store. Survives reloads so a trail viewed at home is still
 * readable at the trailhead. Falls back to memory when IndexedDB is unavailable
 * (private windows, older WebKit, server-side rendering).
 */
export function indexedDbStore(dbName = "viamissa", storeName = "cache"): CacheStore {
  if (typeof indexedDB === "undefined") return memoryStore();

  let dbPromise: Promise<IDBDatabase> | undefined;

  const open = (): Promise<IDBDatabase> => {
    dbPromise ??= new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(dbName, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(storeName)) {
          request.result.createObjectStore(storeName);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    return dbPromise;
  };

  const run = <T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest): Promise<T> =>
    open().then(
      (db) =>
        new Promise<T>((resolve, reject) => {
          const tx = db.transaction(storeName, mode);
          const request = fn(tx.objectStore(storeName));
          request.onsuccess = () => resolve(request.result as T);
          request.onerror = () => reject(request.error);
        }),
    );

  const memoryFallback = memoryStore();

  return {
    async get(key) {
      try {
        return await run<CacheEntry | undefined>("readonly", (store) => store.get(key));
      } catch {
        return memoryFallback.get(key);
      }
    },
    async set(key, entry) {
      try {
        await run("readwrite", (store) => store.put(entry, key));
      } catch {
        await memoryFallback.set(key, entry);
      }
    },
    async delete(key) {
      try {
        await run("readwrite", (store) => store.delete(key));
      } catch {
        await memoryFallback.delete(key);
      }
    },
    async clear() {
      try {
        await run("readwrite", (store) => store.clear());
      } catch {
        await memoryFallback.clear();
      }
    },
    async keys() {
      try {
        return await run<string[]>("readonly", (store) => store.getAllKeys() as IDBRequest);
      } catch {
        return memoryFallback.keys();
      }
    },
  };
}
